/**
 * Membership Card State Machine
 *
 * This module defines a simplified state machine for membership card behavior.
 * Each state represents a specific combination of user status, plan relationship,
 * and available actions. The design uses 11 core states with attributes for
 * variations instead of excessive state enumeration.
 */

import type { TFunction } from 'i18next'
import { isMatching, P } from 'ts-pattern'
import { Interval } from 'src/services/apollo/generated/graphql'
import {
  isMobilePaymentProvider,
  PaymentProvider,
} from 'src/services/helpers/payment'

// ============================================================================
// State Definitions
// ============================================================================

/**
 * Core enumeration of membership card states.
 * Each state represents a unique combination of:
 * - User subscription status
 * - Plan tier relationship
 * - Payment status
 * - Available actions
 *
 * States are designed to be minimal and comprehensive, using attributes
 * for variations rather than separate states.
 */
export enum MembershipCardState {
  LOADING,

  // Core user states
  NOT_LOGGED_IN,
  SUBSCRIBE_NEW,

  // Current plan states
  CURRENT_PLAN_SUPPORT_RENEW,
  CURRENT_PLAN_NON_CANCELLABLE,
  CURRENT_PLAN_CANCELLABLE,

  // Plan change states
  UPGRADE_AVAILABLE,
  DOWNGRADE_AVAILABLE,

  // System restriction states
  PAYMENT_UNAVAILABLE,
  PROVIDER_ISSUES,
  PROVIDER_EXTERNALLY_MANAGED,
}

// ============================================================================
// State Properties
// ============================================================================

export interface CardButtonProps {
  label: string
  buttonType: 'primary' | 'secondary' | 'danger' | 'disabled'
  isDisabled: boolean
  disabledReason?: string
  action: MembershipCardStateAction
}

export enum MembershipCardStateAction {
  LOGIN = 'LOGIN',
  SUBSCRIBE = 'SUBSCRIBE',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  VIEW_PLAN = 'VIEW_PLAN',
  CANCEL = 'CANCEL',
  RESUBSCRIBE = 'RESUBSCRIBE',
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  NONE = 'NONE',
}

export interface MembershipCardStateInfo {
  // User
  isLoggedIn: boolean

  // Membership
  currentTier: number | null
  targetTier: number

  // Subscription
  hasActiveSubscription: boolean
  subscriptionStatus?: string
  isCanceled: boolean

  // Payment
  paymentProvider?: PaymentProvider | string
  isPaymentAvailable: boolean
  isUpgradeDowngradeSupported: boolean

  // Business rules
  enableCancel: boolean

  subscriptionEndDate?: string

  // Interval information for upgrade/downgrade restrictions
  currentInterval?: Interval
  targetInterval: Interval
  isSameInterval: boolean

  // Mobile platform
  mobilePlatform?: 'iOS' | 'Android'

  // Additional attributes for merged states
}

// ============================================================================
// State Determination
// ============================================================================

/**
 * Determines the current state based on the info.
 * This is the core state machine logic with simplified state handling.
 *
 * Priority order:
 * 1. Authentication status
 * 2. Payment availability
 * 3. Provider restrictions
 * 4. Payment pending status
 * 6. Current plan relationship
 * 7. Plan change availability
 */
export function determineState(
  info: MembershipCardStateInfo | undefined,
): MembershipCardState {
  if (info == null) {
    return MembershipCardState.LOADING
  }

  // Authentication check - highest priority
  if (!info.isLoggedIn) {
    return MembershipCardState.NOT_LOGGED_IN
  }

  // Payment system availability
  if (!info.isPaymentAvailable) {
    return MembershipCardState.PAYMENT_UNAVAILABLE
  }

  if (info.hasActiveSubscription) {
    // Provider issues check (combines unknown provider and change restrictions)
    if (
      info.paymentProvider === 'unknown' ||
      !info.isUpgradeDowngradeSupported
    ) {
      return MembershipCardState.PROVIDER_ISSUES
    }

    if (
      isMatching(
        P.union(
          {
            paymentProvider: PaymentProvider.apple,
            mobilePlatform: P.not('iOS'),
          },
          {
            paymentProvider: PaymentProvider.google,
            mobilePlatform: P.not('Android'),
          },
          {
            paymentProvider: P.not(P.when(isMobilePaymentProvider)),
            mobilePlatform: P.string.minLength(1),
          },
        ),
        info,
      )
    ) {
      return MembershipCardState.PROVIDER_EXTERNALLY_MANAGED
    }

    const tierDiff = info.targetTier - (info.currentTier ?? 0)

    // Upgrade
    if (
      tierDiff > 0 ||
      (info.currentInterval === Interval.Month &&
        info.targetInterval === Interval.Year)
    ) {
      return MembershipCardState.UPGRADE_AVAILABLE
    }
    // Downgrade
    else if (
      tierDiff < 0 ||
      (info.currentInterval === Interval.Year &&
        info.targetInterval === Interval.Month)
    ) {
      return MembershipCardState.DOWNGRADE_AVAILABLE
    }
    // Current plan states
    else if (tierDiff === 0) {
      // FIXME: This is not being used, but should
      if (info.enableCancel) {
        return MembershipCardState.CURRENT_PLAN_CANCELLABLE
      }
      if (
        info.currentInterval === Interval.Year &&
        // Currently not supporting manual renew on mobile platforms
        !info.mobilePlatform
      ) {
        return MembershipCardState.CURRENT_PLAN_SUPPORT_RENEW
      }
      return MembershipCardState.CURRENT_PLAN_NON_CANCELLABLE
    }
  }

  // Default to new subscription
  return MembershipCardState.SUBSCRIBE_NEW
}

/**
 * Determines the complete state and UI properties for a membership card.
 * This function serves as the Single Source of Truth (SSOT) for membership card behavior,
 * combining state determination with UI property mapping in a single, unified function.
 *
 * Returns both the determined state and all UI properties needed for rendering.
 */
export function getMembershipCardState(
  info: MembershipCardStateInfo | undefined,
  t: TFunction,
): {
  state: MembershipCardState
  uiProps: CardButtonProps
} {
  // Determine the base state using core logic
  const state = determineState(info)

  // Get base UI properties for the state
  const baseProps = getBaseCardButtonProps(state, t)

  // Apply contextual overrides and business rules to create final UI properties
  const uiProps = applyUiOverrides(state, baseProps, info, t)

  return { state, uiProps }
}

/**
 * Applies contextual overrides and business rules to base UI properties.
 * This function handles all the conditional logic that modifies button behavior
 * based on subscription status, payment provider restrictions, and other business rules.
 */
function applyUiOverrides(
  state: MembershipCardState,
  baseProps: CardButtonProps,
  info: MembershipCardStateInfo | undefined,
  t: TFunction,
): CardButtonProps {
  if (state === MembershipCardState.LOADING || info == null) {
    return baseProps
  }

  // Apply provider-specific restrictions
  if (state === MembershipCardState.PROVIDER_ISSUES) {
    const disabledReason =
      info.paymentProvider === 'unknown'
        ? t('membership:plan.state.disabled-by-payment-provider.label')
        : // oxfmt-ignore
          t('membership:plan.state.payment-provider-not-support-updowngrade.label')

    return {
      ...baseProps,
      isDisabled: true,
      disabledReason,
    }
  }
  if (state === MembershipCardState.PROVIDER_EXTERNALLY_MANAGED) {
    return {
      ...baseProps,
      isDisabled: true,
      disabledReason: t(
        'membership:plan.state.disabled-by-payment-provider.label',
      ),
    }
  }

  const tierDiff = info.targetTier - (info.currentTier ?? 0)

  // currently in subscription
  if (info.hasActiveSubscription) {
    // Interval change can be divided to four situation
    // 1. month -> month
    // 2. month -> year
    // 3. year  -> month (×)
    // 4. year  -> year

    // year subscription to month subscription is not allowed on web.
    if (
      info.currentInterval === Interval.Year &&
      info.targetInterval === Interval.Month &&
      !isMobilePaymentProvider(info.paymentProvider)
    ) {
      return {
        ...baseProps,
        isDisabled: true,
        disabledReason: t('membership:plan.state.downgrade-is-disabled.label'),
      }
    }

    // support same tier month switch to year
    if (
      info.currentInterval === Interval.Month &&
      info.targetInterval === Interval.Year &&
      tierDiff >= 0
    ) {
      return {
        label: t('membership:plan.action.upgrade.label'),
        buttonType: 'primary',
        isDisabled: false,
        action: MembershipCardStateAction.UPGRADE,
      }
    }

    // Downgrade prohibition on web
    if (tierDiff < 0 && !isMobilePaymentProvider(info.paymentProvider)) {
      return {
        ...baseProps,
        isDisabled: true,
        disabledReason: t('membership:plan.state.downgrade-is-disabled.label'),
      }
    }

    // Provider doesn't support upgrade/downgrade
    if (!info.isUpgradeDowngradeSupported && tierDiff !== 0) {
      return {
        ...baseProps,
        isDisabled: true,
        disabledReason: t(
          'membership:plan.state.payment-provider-not-support-updowngrade.label',
        ),
      }
    }
  }

  return baseProps
}

function getBaseCardButtonProps(
  state: MembershipCardState,
  t: TFunction,
): CardButtonProps {
  const stateMap: Record<MembershipCardState, CardButtonProps> = {
    [MembershipCardState.LOADING]: {
      label: t('common:hint.loading'),
      buttonType: 'disabled',
      isDisabled: true,
      action: MembershipCardStateAction.NONE,
    },

    [MembershipCardState.NOT_LOGGED_IN]: {
      label: t('membership:plan.action.subscribe.label'),
      buttonType: 'primary',
      isDisabled: false,
      action: MembershipCardStateAction.LOGIN,
    },

    [MembershipCardState.SUBSCRIBE_NEW]: {
      label: t('membership:plan.action.subscribe.label'),
      buttonType: 'primary',
      isDisabled: false,
      action: MembershipCardStateAction.SUBSCRIBE,
    },

    [MembershipCardState.CURRENT_PLAN_SUPPORT_RENEW]: {
      label: t('membership:plan.action.renew.label'),
      buttonType: 'primary',
      isDisabled: false,
      action: MembershipCardStateAction.SUBSCRIBE,
    },

    [MembershipCardState.CURRENT_PLAN_NON_CANCELLABLE]: {
      label: t('membership:plan.state.subscribed.label'),
      buttonType: 'secondary',
      isDisabled: true,
      // The label is self-explanatory, no need for a separate reason
      // disabledReason: '',
      action: MembershipCardStateAction.NONE,
    },

    [MembershipCardState.CURRENT_PLAN_CANCELLABLE]: {
      label: t('membership:plan.action.cancel.label'),
      buttonType: 'danger',
      isDisabled: false,
      action: MembershipCardStateAction.CANCEL,
    },

    [MembershipCardState.UPGRADE_AVAILABLE]: {
      label: t('membership:plan.action.upgrade.label'),
      buttonType: 'primary',
      isDisabled: false,
      action: MembershipCardStateAction.UPGRADE,
    },

    [MembershipCardState.DOWNGRADE_AVAILABLE]: {
      label: t('membership:plan.action.downgrade.label'),
      buttonType: 'secondary',
      isDisabled: false,
      action: MembershipCardStateAction.DOWNGRADE,
    },

    [MembershipCardState.PAYMENT_UNAVAILABLE]: {
      label: t('membership:plan.action.subscribe.label'),
      buttonType: 'disabled',
      isDisabled: true,
      disabledReason: t('membership:plan.state.payment-unavailable.label'),
      action: MembershipCardStateAction.NONE,
    },

    [MembershipCardState.PROVIDER_ISSUES]: {
      label: t('membership:plan.action.subscribe.label'),
      buttonType: 'disabled',
      isDisabled: true,
      action: MembershipCardStateAction.NONE,
    },

    [MembershipCardState.PROVIDER_EXTERNALLY_MANAGED]: {
      label: t('membership:plan.action.subscribe.label'),
      buttonType: 'disabled',
      isDisabled: true,
      action: MembershipCardStateAction.NONE,
    },
  }

  return stateMap[state]
}
